import type { QrBatch } from '../api/qrGeneration'
import { safeQrFilename } from '../model/qrGeneration'

/** Render every code and download a cut-ready A4 PDF. Heavy libraries are loaded only on demand. */
export async function downloadQrBatchPdf(
  batch: QrBatch,
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (batch.codes.length === 0) return
  const [{ default: QRCode }, { jsPDF }] = await Promise.all([import('qrcode'), import('jspdf')])
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 10
  // 5 × 5 = 25 labels per A4 page, each a small QR with its code above and below.
  const columns = 5
  const rowsPerPage = 5
  const cellWidth = (pageWidth - margin * 2) / columns
  const cellHeight = (pageHeight - margin * 2) / rowsPerPage
  const qrSize = 24
  const textGap = 2.5
  const lineHeight = 3.5
  const labelHeight = lineHeight + textGap + qrSize + textGap + lineHeight
  const perPage = columns * rowsPerPage
  doc.setFont('helvetica', 'normal')

  for (let index = 0; index < batch.codes.length; index += 1) {
    const code = batch.codes[index]!
    const indexOnPage = index % perPage
    if (indexOnPage === 0) {
      if (index > 0) doc.addPage()
      doc.setFontSize(9)
      doc.setTextColor(100)
      doc.text(
        `${batch.kind === 'SEAL' ? 'Seal strip' : 'Bag tags'} · ${batch.villageName} · ${batch.id.slice(0, 8)}`,
        margin,
        6,
      )
    }
    const col = indexOnPage % columns
    const row = Math.floor(indexOnPage / columns)
    const x = margin + col * cellWidth
    const y = margin + row * cellHeight + (cellHeight - labelHeight) / 2
    const centre = x + cellWidth / 2
    const image = await QRCode.toDataURL(code.payload, {
      width: 320,
      margin: 0,
      errorCorrectionLevel: 'M',
    })
    doc.setFontSize(labelFontSize(doc, code.payload, qrSize))
    doc.setTextColor(0)
    const qrTop = y + lineHeight + textGap
    doc.text(code.payload, centre, y + lineHeight, { align: 'center' })
    doc.addImage(image, 'PNG', centre - qrSize / 2, qrTop, qrSize, qrSize)
    doc.text(code.payload, centre, qrTop + qrSize + textGap + lineHeight, { align: 'center' })
    onProgress?.(index + 1, batch.codes.length)
  }

  const kind = batch.kind === 'SEAL' ? 'seals' : 'bag-tags'
  doc.save(
    `${kind}-${safeQrFilename(batch.villageName)}-${batch.startSequence}-${batch.endSequence}.pdf`,
  )
}

/** The largest size (up to 8 pt) at which the code is no wider than the QR under it. */
function labelFontSize(
  doc: { getTextWidth: (t: string) => number; setFontSize: (n: number) => unknown },
  text: string,
  width: number,
): number {
  doc.setFontSize(10)
  const at10 = doc.getTextWidth(text)
  return Math.min(8, Math.max(5, (10 * width) / at10))
}
